import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_INITIALIZER, EnvironmentProviders, ErrorHandler, inject, makeEnvironmentProviders } from '@angular/core';
import { TokenRefreshIntereptor } from '../user/services/interceptors/token-refresh.interceptor';
import { AuthService } from '../user/services/auth.service';
import { FirstTimeRegistrationInterceptor } from '../shared/services/interceptors/first-time-registration.interceptor';
import { MemberQuery } from '../account/member/member.query';
import { RegistrationStepService } from '../registration/registration-step/registration-step.service';
import { LoggingService } from '../shared/services/core/logging.service';
import { Router } from '@angular/router';
import { HttpErrorInterceptor } from '../shared/services/interceptors/http-error.interceptor';
import { ErrorStateService } from '../shared/services/core/error.service';
import { TranslateService } from '@ngx-translate/core';
import { DATE_OPTIONS, DEFAULT_DATE_OPTIONS } from '../shared/date-option';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { MaintenanceGuard } from '../shared/services/core/maintance.guard';
import { CookieService } from 'ngx-cookie-service';
import { AuthGuard } from '../user/services/auth.guard';
import { AppProviderConfig } from './models/app-config.model';
import { TranslationLoader } from '../shared/services/core/i18n/translationloader.service';
import { LANGUAGE } from '../shared/models/language.model';
import { FgbErrorHandler } from '../shared/services/core/fgberror-handler';
import { DEFAULT_COUNTRY } from '../registration/models/default-country.token';
import { ThemeService } from '../shared/services/core/theme.service';
import { APP_FEATURE } from '../shared/feature.token';
import { PAYMENT_TEMPLATES } from '../shared/models/orders/payment-templates.token';
import { PAYMENT_PROVIDER } from '../shared/models/orders/payment-provider.token';
import { ProviderName } from '../account/models/login-response.model';
import { COMPONENT_VERSIONS, provideVersionedService } from '../shared/component-versions.token';
import { LOTTO_SOURCE, MARKETPLACE_SOURCE } from '../loyalty/shared/sources/loyalty-source.model';
import { MarketplaceV1Source, MarketplaceV2Source } from '../loyalty/shared/sources/marketplace.source';
import { LottoV1Source, LottoV2Source } from '../loyalty/shared/sources/lotto.source';

/**
 * Configurations for the application to work.
 * should be provided in the root module
 * @example
 *  providers: [
 *       provideAppConfig({
 *           dateOptions: {
 *               LongDateFormat: 'MMM D, yyyy h:mma'
 *           }
 *       }),
 *   ...
 *   ]
 */
export function provideAppConfig(appConfig: AppProviderConfig): EnvironmentProviders {
  // intialize defaults
  const dateOptions = { ...DEFAULT_DATE_OPTIONS, ...appConfig.dateOptions };
  const defaultCountry = appConfig.defaultCountry || '';
  const defaultFeature = appConfig.feature || { enableDarkMode: false };
  const paymentProvider = appConfig.paymentProvider ?? ProviderName.FreedomPay;

  return makeEnvironmentProviders([
    DatePipe,
    CurrencyPipe,
    MaintenanceGuard,
    CookieService,
    AuthGuard,
    AuthService,
    {
      provide: DATE_OPTIONS,
      useValue: dateOptions,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenRefreshIntereptor,
      multi: true,
      deps: [AuthService],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: FirstTimeRegistrationInterceptor,
      multi: true,
      deps: [MemberQuery, RegistrationStepService, LoggingService, Router],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
      deps: [ErrorStateService, LoggingService, TranslateService, Router, AuthService],
    },
    {
      provide: DEFAULT_COUNTRY,
      useValue: defaultCountry,
    },
    {
      provide: PAYMENT_TEMPLATES,
      useValue: appConfig.paymentTemplates,
    },
    {
      provide: PAYMENT_PROVIDER,
      useValue: paymentProvider,
    },
    {
      provide: APP_FEATURE,
      useValue: defaultFeature,
    },
    {
      provide: COMPONENT_VERSIONS,
      useValue: appConfig.componentVersions || {},
    },
    {
      provide: ErrorHandler,
      useClass: FgbErrorHandler,
    },
    // Languages for the language selector, if language does not exist in the default languages
    // just provide the LANGUAGE in the app module
    // ex:
    // {
    //   provide: LANGUAGE,
    //   useValue: { label: 'TEST', lang: 'en-US', lcid: '1234' },
    //   multi: true
    // }
    //
    {
      provide: LANGUAGE,
      useValue: { label: 'fgbcl.translate.lang.en.label', lang: 'en-GB', lcid: '2057', flag: 'GB', currencyCode: 'GBP' },
      multi: true,
    },
    {
      provide: LANGUAGE,
      useValue: { label: 'fgbcl.translate.lang.fr.label', lang: 'fr-FR', lcid: '1036', flag: 'FR', currencyCode: 'EUR' },
      multi: true,
    },
    {
      provide: LANGUAGE,
      useValue: { label: 'fgbcl.translate.lang.fr.ca.label', lang: 'fr-CA', lcid: '3084', flag: 'FR', currencyCode: 'CAD' },
      multi: true,
    },
    {
      provide: LANGUAGE,
      useValue: { label: 'fgbcl.fgbcl.translate.lang.he.label', lang: 'he-IL', lcid: '1037', flag: 'IL', currencyCode: 'ILS' },
      multi: true,
    },
    {
      provide: LANGUAGE,
      useValue: { label: 'fgbcl.translate.lang.gr.label', lang: 'el-GR', lcid: '1032', flag: 'GR', currencyCode: 'EUR' },
      multi: true,
    },
    {
      provide: LANGUAGE,
      useValue: { label: 'fgbcl.translate.lang.us.label', lang: 'en-US', lcid: '1033', flag: 'US', currencyCode: 'USD' },
      multi: true,
    },
    {
      provide: LANGUAGE,
      useValue: { label: 'fgbcl.translate.lang.en.ca.label', lang: 'en-CA', lcid: '4105', flag: 'US', currencyCode: 'CAD' },
      multi: true,
    },
    {
      provide: LANGUAGE,
      useValue: { label: 'fgbcl.translate.lang.ire.label', lang: 'en-IE', lcid: '6153', flag: 'IE', currencyCode: 'EUR' },
      multi: true,
    },
    {
      provide: LANGUAGE,
      useValue: { label: 'fgbcl.translate.lang.spa.us.label', lang: 'es-US', lcid: '21514', flag: 'ES', currencyCode: 'USD' },
      multi: true,
    },
    {
      provide: LANGUAGE,
      useValue: { label: 'fgbcl.translate.lang.ger.swi.label', lang: 'de-CH', lcid: '2055', flag: 'GE', currencyCode: 'EUR' },
      multi: true,
    },
    // Versioning of services for portal components, to allow for multiple versions of a service to be provided and used in the same application.
    // Marketplace and lotto are versioned independently, so any combination of the two is valid.
    provideVersionedService('marketplace', MARKETPLACE_SOURCE, { 1: MarketplaceV1Source, 2: MarketplaceV2Source }, MarketplaceV1Source),
    provideVersionedService('lotto', LOTTO_SOURCE, { 1: LottoV1Source, 2: LottoV2Source }, LottoV1Source),
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        // Initialize translation
        inject(TranslationLoader).init();
        // Initialize theme
        inject(ThemeService).init();
        return () => {};
      },
      multi: true,
    },
  ]);
}
